import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingUser } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../../core/DataTable.jsx";

import PaginationButtons from "../../core/PaginationButtons.jsx";
import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import useDataColumns from "../../../hooks/useDataColumns.jsx";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import { useAuthInfo } from "../../../context/AuthContext.jsx";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";

const orgEndpoint = "/organizations?limit=10&page=1";

const OrganizationsListPage = (props) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [searchTerm, setSearchTerm] = useSearchTerm();

  const history = useHistory();
  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);
    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);
  const { userInfo } = useAuthInfo();
  const { organizationColumns: columns } = useDataColumns();

  const { fetchData, data, isLoading } = useTriggeredAPICall();
  const baseUrl = "/organizations";
  const { currentData, recordsData, listPage } = usePagination(
    data,
    baseUrl,
    fetchData
  );

  const showLoading = useDelayedLoading(isLoading);

  useEffect(() => {
    let selected = [];
    if (props.columns) {
      selected = props.columns.map((col) => ({
        ...columns[col.id],
        width: col.width || undefined,
        minWidth: col.minWidth || undefined,
      }));
    } else {
      selected = [
        { ...columns.name, width: "250x" },
        { ...columns.city, width: "150px" },
        { ...columns.state, width: "100px" },
        { ...columns.is_active, width: "80px" },
      ];
    }
    setSelectedColumns(selected);
  }, [columns, props.columns]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  useEffect(() => {
    const url = searchDebounce
      ? `/organizations/search?q=${searchDebounce}`
      : orgEndpoint;

    fetchData(url);
  }, [searchDebounce, fetchData]);

  if (showLoading && !data) {
    return <Loading />;
  }

  if (!currentData) {
    return null;
  }

  return (
    <div className="flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faBuildingUser} className="organizations-icon" />{" "}
        Organizations
      </h1>
      {!props.hideSearch && (
        <div className="flex-space-between">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="width-200px"
          />

          {userInfo.role.name === "super-admin" && (
            <Button to="/organization-form">+ New Organization</Button>
          )}
        </div>
      )}

      <Loading isLoading={showLoading}>
        <DataTable
          columns={selectedColumns}
          data={props.orgList || currentData}
          loading={showLoading}
          onPageChange={listPage}
          recordsData={recordsData}
          baseUrl={baseUrl}
          setSortParams={setSortParams}
          sortParams={sortParams}
          defaultSortFieldId={sortParams?.sortBy}
          defaultSortAsc={sortParams?.sortDir !== "desc"}
        />
      </Loading>
    </div>
  );
};

export default OrganizationsListPage;
